// Client-side image compression — cuts evidence-photo storage 60-80% with no visible loss.
// Resizes to maxDim on the longest edge and re-encodes as JPEG at the given quality.
export async function compressImage(file, { maxDim = 1600, quality = 0.7 } = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file; // keep original if not smaller
    return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

const PHASE_LABEL = { pre_disaster: "PRE-DISASTER", damage: "DAMAGE", repair: "REPAIR", signage: "SIGNAGE" };

// Burns a GPS/time watermark bar onto the photo (QRA/NSW RA evidence style).
export async function stampWatermark(file, { phase, latitude, longitude, gps_accuracy, bearing } = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const w = bitmap.width, h = bitmap.height;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    const fs = Math.max(14, Math.round(w * 0.021));
    const pad = Math.round(fs * 0.6);
    const lh = Math.round(fs * 1.35);
    const ts = new Date().toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
    const line1 = `MDAu FieldClaim · ${PHASE_LABEL[phase] || String(phase || "").toUpperCase()} · ${ts}`;
    const line2 = latitude != null && longitude != null
      ? `GPS ${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)} ±${gps_accuracy ?? "?"}m${bearing != null && bearing !== "" ? ` · BRG ${Math.round(bearing)}°` : ""}`
      : "GPS UNAVAILABLE AT CAPTURE";
    const barH = lh * 2 + pad * 2;
    ctx.fillStyle = "rgba(10, 14, 26, 0.72)";
    ctx.fillRect(0, h - barH, w, barH);
    ctx.font = `bold ${fs}px 'Courier New', monospace`;
    ctx.fillStyle = "#D4AF37";
    ctx.fillText(line1, pad, h - barH + pad + fs);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(line2, pad, h - barH + pad + fs + lh);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return file;
    return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}
